import { useState, useEffect, useMemo } from 'react';
import { toWaNumber, isValidWaNumber } from '../../utils/phone';
import { ArrowLeft, Download, Mail, MessageCircle, Check, Users, Search, Play } from 'lucide-react';
import { GuidedSendModal } from './GuidedSendModal';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchBlob = async (url) => (await axios.get(url, { responseType: 'blob' })).data;
const fetchBlobUrl = async (url) => URL.createObjectURL(await fetchBlob(url));

const downloadBlob = async (url, filename) => {
  const href = await fetchBlobUrl(url);
  const a = document.createElement('a');
  a.href = href; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
};

const PosterPreview = ({ campaign, onPoster }) => {
  const [fmt, setFmt] = useState('story');
  const [page, setPage] = useState(0);
  const [src, setSrc] = useState(null);
  const pages = Math.max(1, Math.ceil(campaign.product_ids.length / (fmt === 'story' ? 6 : 4)));

  useEffect(() => {
    let alive = true;
    setSrc(null);
    fetchBlob(`${API_URL}/api/admin/newsletter/campaigns/${campaign.id}/poster.png?format=${fmt}&page=${page}`)
      .then(b => { if (!alive) return; const u = URL.createObjectURL(b); setSrc(u); onPoster?.({ blob: b, url: u }); })
      .catch(() => toast.error("Impossible de générer l'affiche"));
    return () => { alive = false; };
  }, [campaign.id, fmt, page]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border border-white/10 bg-[color:var(--color-surface)] p-4" data-testid="poster-preview">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {['story', 'square'].map(f => (
          <button type="button" key={f} onClick={() => { setFmt(f); setPage(0); }}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${fmt === f ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]' : 'border-white/10 text-zinc-400'}`}
            data-testid={`poster-format-${f}`}>
            {f === 'story' ? 'Story 9:16' : 'Carré 1:1'}
          </button>
        ))}
        {pages > 1 && (
          <select value={page} onChange={(e) => setPage(Number(e.target.value))}
            className="bg-[color:var(--color-surface)] border border-white/10 text-zinc-300 text-xs px-2 py-1.5" data-testid="poster-page">
            {Array.from({ length: pages }).map((_, i) => <option key={i} value={i}>{`Page ${i + 1}/${pages}`}</option>)}
          </select>
        )}
        <button type="button"
          onClick={() => downloadBlob(`${API_URL}/api/admin/newsletter/campaigns/${campaign.id}/poster.png?format=${fmt}&page=${page}`, `affiche-${campaign.title}-${fmt}-${page + 1}.png`)}
          className="ml-auto inline-flex items-center gap-2 bg-[color:var(--color-primary)] text-black text-xs px-3 py-1.5 hover:bg-[color:var(--color-primary-hover)]"
          data-testid="poster-download-btn">
          <Download className="w-3.5 h-3.5" /> Télécharger
        </button>
      </div>
      <div className={`mx-auto bg-black ${fmt === 'story' ? 'max-w-[300px] aspect-[9/16]' : 'max-w-[420px] aspect-square'}`}>
        {src ? <img src={src} alt="Affiche" className="w-full h-full object-contain" data-testid="poster-image" />
             : <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm">Génération...</div>}
      </div>
    </div>
  );
};

export const CampaignBroadcast = ({ campaign, recipients, onBack, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [sent, setSent] = useState(new Set(campaign.whatsapp_sent || []));
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [poster, setPoster] = useState(null);
  const [guided, setGuided] = useState(null);
  const emailStatus = campaign.email_status;

  useEffect(() => { setSent(new Set(campaign.whatsapp_sent || [])); }, [campaign]);
  useEffect(() => {
    if (emailStatus?.state !== 'sending') return;
    const t = setInterval(onRefresh, 3000);
    return () => clearInterval(t);
  }, [emailStatus, onRefresh]);

  const list = useMemo(() => {
    const q = search.toLowerCase();
    return recipients.filter(r => !q || `${r.name} ${r.phone} ${r.email}`.toLowerCase().includes(q));
  }, [recipients, search]);

  const waUrl = (r) => {
    const digits = toWaNumber(r.phone);
    const msg = `Bonjour ${r.name?.split(' ')[0] || ''} 👋\n\n${campaign.message}`;
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
  };

  const markSent = async (r, value) => {
    setSent(prev => { const n = new Set(prev); value ? n.add(r.id) : n.delete(r.id); return n; });
    axios.post(`${API_URL}/api/admin/newsletter/campaigns/${campaign.id}/whatsapp-sent`, { customer_id: r.id, sent: value }).catch(() => {});
  };

  const toggleSel = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(list.map(r => r.id)));
  const selectUnsent = () => setSelected(new Set(list.filter(r => !sent.has(r.id)).map(r => r.id)));
  const startGuided = () => {
    const targets = list.filter(r => selected.has(r.id));
    if (targets.length === 0) { toast.error('Sélectionnez au moins un client'); return; }
    setGuided(targets);
  };

  const sendEmail = async () => {
    if (!window.confirm(`Envoyer la newsletter par e-mail aux clients qui ont une adresse ?`)) return;
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/newsletter/campaigns/${campaign.id}/send-email`);
      toast.success(res.data.message);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSendingEmail(false);
    }
  };

  const withEmail = recipients.filter(r => r.email).length;

  return (
    <div data-testid="campaign-broadcast">
      <button type="button" onClick={onBack} className="text-zinc-500 hover:text-white text-sm mb-4 inline-flex items-center gap-2" data-testid="campaign-back-btn">
        <ArrowLeft className="w-4 h-4" /> Nouvelle campagne
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <PosterPreview campaign={campaign} onPoster={setPoster} />
          <div className="border border-white/10 bg-[color:var(--color-surface)] p-4">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Message WhatsApp</p>
            <pre className="text-zinc-300 text-xs whitespace-pre-wrap font-sans max-h-48 overflow-y-auto" data-testid="campaign-message">{campaign.message}</pre>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(campaign.message); toast.success('Message copié'); }}
              className="mt-2 text-[color:var(--color-primary)] text-xs hover:underline" data-testid="campaign-copy-message">Copier le message</button>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="border border-[color:rgb(var(--primary-rgb)/0.30)] bg-[color:rgb(var(--primary-rgb)/0.05)] p-4 text-sm text-zinc-300 space-y-2" data-testid="broadcast-howto">
            <p className="text-[color:var(--color-primary)] uppercase tracking-widest text-xs flex items-center gap-2"><Users className="w-4 h-4" /> Envoyer à tous en une fois (liste de diffusion WhatsApp)</p>
            <ol className="list-decimal list-inside space-y-1 text-zinc-400 text-xs">
              <li>Téléchargez l'affiche et les contacts ci-dessous, puis importez le fichier contacts sur votre téléphone.</li>
              <li>Dans WhatsApp › Listes de diffusion › Nouvelle liste, ajoutez vos clients (max 256 ; ils doivent avoir votre numéro enregistré).</li>
              <li>Envoyez l'affiche + le message copié : chaque client le reçoit individuellement.</li>
            </ol>
            <div className="flex flex-wrap gap-2 pt-1">
              <button type="button" onClick={() => downloadBlob(`${API_URL}/api/admin/newsletter/recipients.vcf`, 'clients-odelicieuse.vcf')}
                className="inline-flex items-center gap-2 border border-[color:rgb(var(--primary-rgb)/0.50)] text-[color:var(--color-primary)] text-xs px-3 py-2 hover:bg-[color:var(--color-primary)] hover:text-black"
                data-testid="download-vcard-btn">
                <Download className="w-3.5 h-3.5" /> Contacts clients (.vcf) — {recipients.length}
              </button>
              <button type="button" onClick={sendEmail} disabled={sendingEmail || emailStatus?.state === 'sending' || withEmail === 0}
                className="inline-flex items-center gap-2 border border-white/20 text-white text-xs px-3 py-2 hover:border-[color:var(--color-primary)] disabled:opacity-40"
                data-testid="send-email-btn">
                <Mail className="w-3.5 h-3.5" />
                {emailStatus?.state === 'sending' ? `Envoi e-mail ${emailStatus.sent + emailStatus.failed}/${emailStatus.total}...`
                  : emailStatus?.state === 'done' ? `E-mail envoyé ${emailStatus.sent}/${emailStatus.total}${emailStatus.failed ? ` (${emailStatus.failed} échec)` : ''} — renvoyer`
                  : `Envoyer par e-mail (${withEmail} client(s))`}
              </button>
            </div>
            {emailStatus?.state === 'done' && emailStatus.failed > 0 && (
              <p className="text-amber-400 text-xs" data-testid="email-failed-hint">
                Des e-mails ont échoué : vérifiez que le domaine odelicieuse.eu est validé dans Resend (l'expéditeur de test ne peut écrire qu'à votre propre adresse).
              </p>
            )}
          </div>

          <div className="border border-white/10 bg-[color:var(--color-surface)]">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b border-white/5">
              <p className="text-white text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#25D366]" /> Envoi individuel — {sent.size}/{recipients.length} envoyés</p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={selectAll} className="text-xs text-zinc-300 border border-white/10 px-2.5 py-1.5 hover:border-[color:var(--color-primary)]" data-testid="select-all-btn">Tout sélectionner</button>
                <button type="button" onClick={selectUnsent} className="text-xs text-zinc-300 border border-white/10 px-2.5 py-1.5 hover:border-[color:var(--color-primary)]" data-testid="select-unsent-btn">Non envoyés</button>
                {selected.size > 0 && (
                  <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-zinc-500 hover:text-white" data-testid="select-none-btn">Désélectionner</button>
                )}
                <button type="button" onClick={startGuided} disabled={selected.size === 0}
                  className="inline-flex items-center gap-1.5 bg-[color:var(--color-primary)] text-black text-xs px-3 py-1.5 hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40"
                  data-testid="guided-send-btn">
                  <Play className="w-3.5 h-3.5" /> Envoi guidé ({selected.size})
                </button>
              </div>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..."
                  className="bg-transparent border border-white/10 text-white text-xs pl-8 pr-2 py-1.5 outline-none w-44" data-testid="recipients-search" />
              </div>
            </div>
            <ul className="divide-y divide-white/5 max-h-[480px] overflow-y-auto" data-testid="recipients-list">
              {list.map(r => {
                const done = sent.has(r.id);
                return (
                  <li key={r.id} className={`flex items-center gap-3 px-3 py-2 ${done ? 'opacity-50' : ''} ${selected.has(r.id) ? 'bg-[color:rgb(var(--primary-rgb)/0.05)]' : ''}`} data-testid={`recipient-${r.id}`}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} className="accent-[color:var(--color-primary)]" data-testid={`recipient-select-${r.id}`} />
                    <button type="button" onClick={() => markSent(r, !done)} title={done ? 'Marquer non envoyé' : 'Marquer envoyé'}
                      className={`w-5 h-5 border flex items-center justify-center ${done ? 'bg-[#25D366] border-[#25D366]' : 'border-white/20'}`}
                      data-testid={`recipient-mark-${r.id}`}>
                      {done && <Check className="w-3.5 h-3.5 text-black" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{r.name}</p>
                      <p className="text-zinc-500 text-xs truncate">{r.phone}{r.email ? ` · ${r.email}` : ''}</p>
                    </div>
                    {isValidWaNumber(r.phone) ? (
                      <a href={waUrl(r)} target="_blank" rel="noreferrer" onClick={() => markSent(r, true)}
                        className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs px-3 py-1.5 hover:bg-[#20BD5A]"
                        data-testid={`recipient-wa-${r.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs px-3 py-1.5 border border-amber-500/40" title="Numéro incomplet : corrigez-le dans Utilisateurs"
                        data-testid={`recipient-wa-invalid-${r.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" /> N° invalide
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      {guided && (
        <GuidedSendModal customers={guided} campaign={campaign} posterBlob={poster?.blob} posterUrl={poster?.url}
          waUrl={waUrl} onMarkSent={markSent} onClose={() => { setGuided(null); setSelected(new Set()); }} />
      )}
    </div>
  );
};
